import React from "react";
import { Link } from "react-router-dom";
import { Calendar, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import EntityActions from "@/components/shared/EntityActions";
import { getStatusBadge } from "@/components/projects/ProjectStatusDropdown";
import { getCustomerDisplayName } from "@/utils/customerDisplayHelpers";
import type { Project } from "@/pages/projects/hooks/useProjectData";

interface ProjectsCardViewProps {
  projects: Project[];
  getProjectActions: (project: Project) => any[];
}

/**
 * ProjectsCardView - Card/grid view for projects list
 * 
 * Displays projects as visual cards in a responsive grid.
 * Each card shows project name, status, description, customer, and dates.
 */
const ProjectsCardView: React.FC<ProjectsCardViewProps> = ({ projects, getProjectActions }) => {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {projects.map((project) => (
        <Card key={project.id} className="hover:shadow-md transition-shadow overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                <Link 
                  to={`/projects/${project.id}`} 
                  className="font-semibold font-montserrat hover:underline truncate"
                >
                  {project.name}
                </Link>
              </div>
              <div className="flex items-center justify-between">
                {getStatusBadge(project.status)}
                <EntityActions actions={getProjectActions(project)} size="sm" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {project.description && (
              <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                {project.description}
              </p>
            )}
            {project.customers && (
              <p className="text-sm text-muted-foreground mb-3">
                <span className="font-medium">Customer:</span> {getCustomerDisplayName(project.customers)}
              </p>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {project.expected_start_date
                  ? `Start: ${project.expected_start_date}`
                  : "No start date"}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {project.expected_end_date
                  ? `End: ${project.expected_end_date}`
                  : "No end date"}
              </div>
            </div>
            <Button asChild variant="outline" size="sm" className="mt-4">
              <Link to={`/projects/${project.id}`}>View Project</Link>
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ProjectsCardView;
